/**
 * Live latch + human next-step copy, always on screen next to the switch
 * (#2575). Field names alone are not the point: the operator needs to
 * know what the next pass will actually do.
 */
import type {
  CodexCapacityAlertSettings,
  CodexCapacityAlertStateResponse,
} from '@nadoutong/contracts';
import { formatLocalTimestamp } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, EmptyState, Panel, Skeleton } from '../../ui/index.ts';
import { describeNextStep, nextStepKey, nextStepParams } from './codexCapacityStatus.ts';

interface Props {
  readonly settings: CodexCapacityAlertSettings | null;
  readonly state: CodexCapacityAlertStateResponse | null;
  readonly loading: boolean;
  readonly error: boolean;
  readonly onRetry: () => void;
}

export function CodexCapacityStatePanel({
  settings,
  state,
  loading,
  error,
  onRetry,
}: Props): JSX.Element {
  const t = useT();

  if (loading) {
    return (
      <Panel caption={t('codex_capacity.state.caption')} padded testId="codex-capacity-state-loading">
        <Skeleton className="h-20 w-full" />
      </Panel>
    );
  }

  if (error || state === null || settings === null) {
    return (
      <Panel caption={t('codex_capacity.state.caption')} padded testId="codex-capacity-state-error">
        <EmptyState
          title={t('codex_capacity.state.error')}
          hint={t('codex_capacity.state.error_hint')}
          action={
            <Button variant="secondary" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          }
        />
      </Panel>
    );
  }

  const next = describeNextStep(settings, state);
  const firing = state.phase === 'firing';

  return (
    <Panel
      caption={t('codex_capacity.state.caption')}
      padded
      testId="codex-capacity-state-panel"
      actions={
        <Chip tone={firing ? 'bad' : 'ok'} testId="codex-capacity-state-phase">
          {t(firing ? 'codex_capacity.state.phase_firing' : 'codex_capacity.state.phase_clear')}
        </Chip>
      }
    >
      <div className="flex flex-col gap-2">
        <p className="m-0 text-dense text-paper-mid" data-testid="codex-capacity-state-window">
          {state.window_pct === null
            ? t('codex_capacity.state.window_undersampled', {
                count: String(state.window_requests),
                min: String(settings.min_window_requests),
              })
            : t('codex_capacity.state.window_ratio', {
                pct: formatPct(state.window_pct),
                count: String(state.window_requests),
              })}
        </p>
        <p className="m-0 text-dense text-paper-mid" data-testid="codex-capacity-state-clear-below">
          {state.clear_below_since === null
            ? t('codex_capacity.state.clear_below_since_none')
            : t('codex_capacity.state.clear_below_since', {
                when: formatLocalTimestamp(state.clear_below_since, { seconds: true }),
              })}
        </p>
        {state.archive_stale ? (
          <p
            className="m-0 rounded border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense text-warn"
            data-testid="codex-capacity-state-archive-stale"
          >
            {t('codex_capacity.state.archive_stale', {
              when:
                state.archive_bucket_start === null
                  ? '—'
                  : formatLocalTimestamp(state.archive_bucket_start, { seconds: true }),
            })}
          </p>
        ) : null}
        <p
          className="m-0 rounded border border-line-soft bg-well px-3 py-2 text-dense text-paper"
          data-testid="codex-capacity-state-next-step"
        >
          {t(nextStepKey(next.kind), nextStepParams(next))}
        </p>
      </div>
    </Panel>
  );
}

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return value.toFixed(1);
}
